import type { CSSProperties } from 'react';
import type { DeadlineRow, DeadlineActivityType } from './types';

// [i18nKey, fallback]. The object keys are the API's own type tokens and stay as the wire sends
// them; the Lokalise keys are English, matching the rest of the deadlines.type.* subtree. The
// fallbacks are the Swedish source copy — they render until Lokalise serves the reader's language.
export const TYPE_LABELS: Record<DeadlineActivityType, [string, string]> = {
  anbudsinlamning: ['deadlines.type.bidSubmission', 'Anbudsinlämning'],
  utvardera_anbud: ['deadlines.type.bidEvaluation', 'Utvärdera anbud'],
  aktivitet: ['deadlines.type.activity', 'Aktivitet'],
  inkop_klart: ['deadlines.type.purchaseCompleted', 'Inköp klart'],
  forfragan_skickas_senast: ['deadlines.type.quotationSentBy', 'Förfrågan skickas senast'],
  ramavtal_utgang: ['deadlines.type.frameworkAgreementExpiry', 'Ramavtal utgångstid'],
};

export const ACTIVITY_TYPES = Object.keys(TYPE_LABELS) as DeadlineActivityType[];

export type ActivityColor = { solid: string; badgeBg: string; badgeText: string };

export const ACTIVITY_COLORS: Record<DeadlineActivityType, ActivityColor> = {
  anbudsinlamning:          { solid: '#3b82f6', badgeBg: '#eff6ff', badgeText: '#1d4ed8' },
  utvardera_anbud:          { solid: '#8b5cf6', badgeBg: '#f5f3ff', badgeText: '#6d28d9' },
  aktivitet:                { solid: '#06b6d4', badgeBg: '#ecfeff', badgeText: '#0e7490' },
  inkop_klart:              { solid: '#22c55e', badgeBg: '#f0fdf4', badgeText: '#15803d' },
  forfragan_skickas_senast: { solid: '#f59e0b', badgeBg: '#fffbeb', badgeText: '#b45309' },
  ramavtal_utgang:          { solid: '#ef4444', badgeBg: '#fef2f2', badgeText: '#b91c1c' },
};

// Feeds the shared .typeDot rule (DeadlinesTab.module.scss): the dot's size and shape live in
// CSS, only its per-type colour comes from here. Falls back to slate for a type this bundle does
// not know — the API owns this vocabulary, so an unmapped value must degrade to a grey dot rather
// than throw during render (the app has no ErrorBoundary). Matches TodoCalendar's colorOf.
export const typeDotStyle = (type: DeadlineActivityType): CSSProperties =>
  ({ '--dot-color': ACTIVITY_COLORS[type]?.solid ?? '#94a3b8' } as CSSProperties);

export const getDisplayType = (row: Pick<DeadlineRow, 'type' | 'daysLeft'>): DeadlineActivityType =>
  row.type === 'anbudsinlamning' && row.daysLeft < 0 ? 'utvardera_anbud' : row.type;

export type UrgencyTone = 'red' | 'yellow' | 'gray';
export const URGENCY_COLORS: Record<UrgencyTone, { bg: string; text: string }> = {
  red: { bg: 'rgb(254, 226, 226)', text: 'rgb(220, 38, 38)' },
  yellow: { bg: 'rgb(254, 249, 195)', text: 'rgb(161, 98, 7)' },
  gray: { bg: 'rgb(243, 244, 246)', text: 'rgb(75, 85, 99)' },
};
export const urgencyTone = (daysLeft: number): UrgencyTone =>
  daysLeft < 0 ? 'red' : daysLeft <= 7 ? 'yellow' : 'gray';

// Feeds the --tone-bg / --tone-text custom properties consumed by .daysLeftBadge,
// .metricCardNumber and .metricCardIcon (DeadlinesTab.module.scss).
export const urgencyStyle = (tone: UrgencyTone): CSSProperties =>
  ({ '--tone-bg': URGENCY_COLORS[tone].bg, '--tone-text': URGENCY_COLORS[tone].text } as CSSProperties);
